/**
 * <SecretsPanel> — engineer-tier Secret view + rotate UI (#251).
 *
 * Lists Secrets in a selectable namespace and opens a per-secret detail
 * drawer where each value can be revealed (auditing on every reveal).
 *
 * Defense-in-depth: this panel is wrapped at the entry point in
 * <RoleGate role={Engineer}>; the BFF + deployment-service mirror also
 * gate every endpoint at Engineer+.
 *
 * Anti-goals (per the issue):
 *   - No cluster-wide secret search (namespace-at-a-time only)
 *   - No editor (view + rotate only — engineers create with kubectl/Helm)
 *   - No bulk rotation across multiple sites
 */
import { useState } from 'react';
import { Lock, ShieldAlert } from 'lucide-react';
import { Role } from '@jeket-cloud/types/auth';
import { useClusterNamespaces, useSecretsList } from './useClusterData';
import { RoleGate } from '@/components/auth/RoleGate';
import SecretDetailDrawer from './SecretDetailDrawer';
import type { SecretMetadata } from '@/api/client';

interface Props {
  siteId: string;
}

const DEFAULT_NAMESPACE = 'jbs';

function PanelInner({ siteId }: Props) {
  const [namespace, setNamespace] = useState<string>(DEFAULT_NAMESPACE);
  const [selected, setSelected] = useState<SecretMetadata | null>(null);

  const { data: namespaces } = useClusterNamespaces(siteId);
  const { data: secrets, isLoading, refetch } = useSecretsList(siteId, { namespace });

  return (
    <div className="space-y-3" data-testid="secrets-panel">
      {/* Audit warning banner — visible to every Engineer who lands here. */}
      <div
        role="status"
        className="flex items-start gap-3 bg-status-warning/10 border border-status-warning/30 rounded-lg px-4 py-3"
      >
        <ShieldAlert size={16} className="text-status-warning shrink-0 mt-0.5" />
        <div className="text-xs text-silver-bright space-y-0.5">
          <p className="font-medium">Every reveal is audited.</p>
          <p className="text-silver-muted">
            Looking at a secret&apos;s value emits a <code className="text-gold">deployment.audit</code> event
            visible to your organization&apos;s admins. Rotating a secret emits another. Use this surface only when
            you need the value to configure a dependent system.
          </p>
        </div>
      </div>

      {/* Namespace selector + manual refresh */}
      <div className="flex items-center gap-3 flex-wrap">
        <label htmlFor="secrets-ns" className="text-xs text-silver-muted">Namespace:</label>
        <select
          id="secrets-ns"
          data-testid="secrets-namespace-select"
          value={namespace}
          onChange={(e) => setNamespace(e.target.value)}
          className="bg-surface border border-surface-elevated rounded-lg px-3 py-1.5 text-xs text-silver-bright focus:border-gold focus:outline-none"
        >
          {/* Always include the default + any others discovered. The
              namespaces query may not have run yet on first paint, so we
              guarantee `jbs` is at least selectable. */}
          {[DEFAULT_NAMESPACE, ...((namespaces ?? []).filter((n) => n !== DEFAULT_NAMESPACE))].map((ns) => (
            <option key={ns} value={ns}>{ns}</option>
          ))}
        </select>
        <button
          onClick={() => refetch()}
          className="text-xs text-silver-muted hover:text-silver-bright transition-colors"
        >
          Refresh
        </button>
      </div>

      {/* Loading + empty + table */}
      {isLoading && !secrets && (
        <div className="bg-surface border border-surface-elevated rounded-xl p-8 space-y-3 animate-pulse">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-8 bg-surface-elevated rounded" />
          ))}
        </div>
      )}

      {!isLoading && (!secrets || secrets.length === 0) && (
        <div className="bg-surface border border-surface-elevated rounded-xl text-center py-12 space-y-3">
          <Lock size={40} className="mx-auto text-silver-muted/30" />
          <p className="text-silver-muted">No secrets in <span className="font-mono text-silver-bright">{namespace}</span>.</p>
        </div>
      )}

      {secrets && secrets.length > 0 && (
        <div className="bg-surface border border-surface-elevated rounded-xl overflow-hidden">
          <table className="w-full text-sm" data-testid="secrets-table">
            <thead className="bg-surface-elevated/30">
              <tr className="text-left text-xs text-silver-muted uppercase tracking-wider">
                <th className="px-4 py-2 font-medium">Name</th>
                <th className="px-4 py-2 font-medium">Type</th>
                <th className="px-4 py-2 font-medium">Keys</th>
                <th className="px-4 py-2 font-medium">Age</th>
              </tr>
            </thead>
            <tbody>
              {secrets.map((s) => (
                <tr
                  key={`${s.namespace}/${s.name}`}
                  data-testid={`secret-row-${s.name}`}
                  className="border-b border-surface-elevated last:border-0 hover:bg-surface-elevated/30 cursor-pointer transition-colors"
                  onClick={() => setSelected(s)}
                >
                  <td className="px-4 py-2.5 text-xs text-silver-bright font-mono">{s.name}</td>
                  <td className="px-4 py-2.5 text-xs text-silver-muted">{s.type}</td>
                  <td className="px-4 py-2.5 text-xs text-silver-muted">
                    {s.keys.length} {s.keys.length === 1 ? 'key' : 'keys'}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-silver-muted">{s.age}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <SecretDetailDrawer
        siteId={siteId}
        secret={selected}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}

export default function SecretsPanel({ siteId }: Props) {
  return (
    <RoleGate
      role={Role.Engineer}
      fallback={
        <div className="bg-surface border border-surface-elevated rounded-xl text-center py-12">
          <p className="text-silver-muted">Secrets are restricted to Engineer+ roles.</p>
        </div>
      }
    >
      <PanelInner siteId={siteId} />
    </RoleGate>
  );
}
